/**
 * Reading the picture a page publishes about itself.
 *
 * Two processes do this (the API with a plain fetch, the automation worker
 * with a browser), so the rules live here rather than twice. Everything in this
 * file was written against what real platforms actually serve, and every rule
 * below exists because one of them broke an earlier version.
 */

/**
 * The preview image a page publishes about itself, or nothing.
 *
 * Read with a regular expression rather than a parser: what is wanted is one
 * attribute of one meta tag, the tag is machine-written by the platform in
 * every case that matters, and the alternative is a DOM parser in the API
 * process to read a string.
 *
 * **Unquoted values are accepted, and that is not pedantry.** HTML5 allows
 * `content=https://…` with no quotes, OnlyFans serves exactly that, and the
 * first version of this required quotes, so the one platform this feature
 * exists for returned "no preview image" while its page plainly had one.
 *
 * `og:image` first because it is what the platforms fill in; `twitter:image`
 * after it because a few sites set only that.
 */
export function previewImageUrl(html: string, pageUrl: string): string | null {
  const head = html.slice(0, 200_000);

  for (const wanted of ['og:image', 'twitter:image']) {
    const tag = new RegExp(
      `<meta[^>]*(?:property|name)=["']?${wanted}["']?[^>]*>`,
      'i',
    ).exec(head);
    if (!tag) continue;

    // Quoted or bare. A bare value ends at whitespace or the closing bracket.
    const content = /content=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(tag[0]);
    const raw = content?.slice(1).find((group) => group !== undefined);
    if (!raw) continue;

    try {
      // Resolved against the page, because a relative `/img/avatar.jpg` is
      // common and an unresolved one is a fetch of a path with no host.
      const absolute = new URL(decodeEntities(raw.trim()), pageUrl);
      if (absolute.protocol !== 'https:' && absolute.protocol !== 'http:') continue;
      return absolute.toString();
    } catch {
      continue;
    }
  }
  return null;
}

/**
 * A URL on the same host whose page cannot belong to anybody.
 *
 * Used to ask what the platform serves when it has no profile to show, which is
 * the only reliable way to tell her avatar from the platform's own branding.
 * Null when the address has no path segment to vary: nothing to compare
 * against, so the caller keeps what it found.
 *
 * The token is fixed rather than random so that two runs ask the same question,
 * and long enough that no real account has it.
 */
export function controlUrlFor(pageUrl: string): string | null {
  try {
    const url = new URL(pageUrl);
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;

    segments[segments.length - 1] = 'zzq7x4k2-no-such-account';
    url.pathname = `/${segments.join('/')}`;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Content types that are the same picture under a different spelling.
 *
 * `image/jpg` is not a registered type and is served anyway (linktr.ee returns
 * it for every avatar it hosts), so a strict list rejected a perfectly good
 * JPEG as "not an image". Normalised rather than added to the allowed list,
 * because what is stored should say what the file actually is.
 */
export function normaliseImageType(contentType: string | undefined): string {
  const type = (contentType ?? '').split(';')[0].trim().toLowerCase();
  if (type === 'image/jpg') return 'image/jpeg';
  if (type === 'image/pjpeg') return 'image/jpeg';
  return type;
}

/**
 * The five entities that appear in a URL written into an HTML attribute.
 *
 * `&amp;` is the one that matters: a preview image with a query string is
 * written with it, and fetching the undecoded form gets a 404 from a CDN that
 * saw two parameters where there was one.
 */
function decodeEntities(value: string): string {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&#38;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}
